import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Campaign } from '../../types';
import { useAuthStore } from '../../store/authStore';
import { MatchApplicationForm } from '../matches/MatchApplicationForm';
import { useLocaleFormatters } from '../../utils/localeFormatters';
import { usePermissions } from '../../hooks/usePermissions';

interface CampaignDetailsModalProps {
  campaign: Campaign;
  isOpen: boolean;
  onClose: () => void;
}

const CONTENT_TYPE_KEY_MAP: Record<string, string> = {
  instagram_post: 'instagramPost', instagram_reels: 'instagramReels',
  youtube: 'youtube', tiktok: 'tiktok', naver_blog: 'naverBlog',
};

const CATEGORY_KEY_MAP: Record<string, string> = {
  hotel: 'hotel', fnb: 'fnb', tour_activity: 'tourActivity',
  spa_wellness: 'spaWellness', aviation_transport: 'aviationTransport', other: 'other',
};

const PROVISION_KEY_MAP: Record<string, string> = {
  on_site: 'onSite', pre_reservation: 'preReservation', voucher: 'voucher', custom: 'custom',
};

export const CampaignDetailsModal: React.FC<CampaignDetailsModalProps> = ({
  campaign, isOpen, onClose
}) => {
  const { t } = useTranslation('campaigns');
  const { hasPermission } = usePermissions();
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const { formatDate, formatNumber } = useLocaleFormatters();

  if (!isOpen) return null;

  const handleApplicationSubmit = (matchId: string) => {
    setApplicationSubmitted(true);
    setShowApplicationForm(false);
  };

  const canApplyToCampaigns = hasPermission('matches:create');
  const canApply = canApplyToCampaigns && campaign.status === 'active' && !applicationSubmitted;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative inline-block w-full max-w-4xl p-6 my-8 text-left transition-all transform bg-white rounded-lg shadow-xl sm:align-middle max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900">{campaign.campaignName || campaign.title || ''}</h2>
              <div className="flex items-center gap-3 mt-2">
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                  campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                  campaign.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>{campaign.status}</span>
                <span className="text-sm text-gray-500">{campaign.createdAt ? formatDate(campaign.createdAt) : ''}</span>
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-500">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Representative Image */}
          {campaign.representativeImages?.[0]?.fileUrl && (
            <div className="mb-6 rounded-lg overflow-hidden">
              <img src={campaign.representativeImages[0].fileUrl} alt={campaign.campaignName || ''} className="w-full h-64 object-cover" />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">

              {/* Advertiser / Brand Info */}
              {(campaign.advertiserName || campaign.brandName) && (
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('registration.sections.advertiserInfo')}</h3>
                  <div className="space-y-2 text-sm">
                    {campaign.advertiserName && (
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-gray-600">{t('registration.fields.advertiserName')}</span>
                        <span className="font-medium">{campaign.advertiserName}</span>
                      </div>
                    )}
                    {campaign.brandName && (
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-gray-600">{t('registration.fields.brandName')}</span>
                        <span className="font-medium">{campaign.brandName}</span>
                      </div>
                    )}
                    {campaign.contactPersonName && (
                      <div className="flex justify-between py-1">
                        <span className="text-gray-600">{t('registration.fields.contactPersonName')}</span>
                        <span className="font-medium">{campaign.contactPersonName}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Campaign Basic Info */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('registration.sections.campaignBasicInfo')}</h3>
                <div className="space-y-2 text-sm">
                  {campaign.campaignType && (
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-gray-600">{t('registration.fields.campaignType')}</span>
                      <span className="font-medium">{t(`registration.campaignTypes.${campaign.campaignType}`, campaign.campaignType)}</span>
                    </div>
                  )}
                  {campaign.campaignCategory && (
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-gray-600">{t('registration.fields.campaignCategory')}</span>
                      <span className="font-medium">{t(`registration.categories.${CATEGORY_KEY_MAP[campaign.campaignCategory] || campaign.campaignCategory}`, campaign.campaignCategory)}</span>
                    </div>
                  )}
                  {campaign.contentTypes && campaign.contentTypes.length > 0 && (
                    <div className="py-1 border-b border-gray-100">
                      <span className="text-gray-600 block mb-2">{t('registration.fields.contentTypes')}</span>
                      <div className="flex flex-wrap gap-1">
                        {campaign.contentTypes.map((ct: string, idx: number) => (
                          <span key={idx} className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full">
                            {t(`registration.contentTypes.${CONTENT_TYPE_KEY_MAP[ct] || ct}`, ct)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {campaign.recruitmentPeriod && (
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-gray-600">{t('registration.fields.recruitmentPeriod')}</span>
                      <span className="font-medium">
                        {formatDate(campaign.recruitmentPeriod.startDate)}
                        {campaign.recruitmentPeriod.alwaysRecruiting ? ` ~ ${t('registration.fields.alwaysRecruiting')}` :
                         campaign.recruitmentPeriod.endDate ? ` ~ ${formatDate(campaign.recruitmentPeriod.endDate)}` : ''}
                      </span>
                    </div>
                  )}
                  {campaign.numberOfRecruits != null && (
                    <div className="flex justify-between py-1">
                      <span className="text-gray-600">{t('registration.fields.numberOfRecruits')}</span>
                      <span className="font-medium">{formatNumber(campaign.numberOfRecruits)}명</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Product / Service Info */}
              {(campaign.productName || campaign.providedValueDescription) && (
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('registration.sections.productServiceInfo')}</h3>
                  <div className="space-y-2 text-sm">
                    {campaign.productName && (
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-gray-600">{t('registration.fields.productName')}</span>
                        <span className="font-medium">{campaign.productName}</span>
                      </div>
                    )}
                    {campaign.introductionPageUrl && (
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-gray-600">{t('registration.fields.introductionPageUrl')}</span>
                        <a href={campaign.introductionPageUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate max-w-[200px]">{campaign.introductionPageUrl}</a>
                      </div>
                    )}
                    {campaign.providedValueDescription && (
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-gray-600">{t('registration.fields.providedValue')}</span>
                        <span className="font-medium">{campaign.providedValueDescription}</span>
                      </div>
                    )}
                    {campaign.provisionMethod && (
                      <div className="flex justify-between py-1">
                        <span className="text-gray-600">{t('registration.fields.provisionMethod')}</span>
                        <span className="font-medium">{t(`registration.provisionMethods.${PROVISION_KEY_MAP[campaign.provisionMethod] || campaign.provisionMethod}`, campaign.provisionMethod)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Publishing Conditions Table */}
              {campaign.publishingConditions && campaign.publishingConditions.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('registration.sections.publishingConditions')}</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-2 py-2 text-left font-medium text-gray-600">{t('registration.publishingTable.sequence')}</th>
                          <th className="px-2 py-2 text-left font-medium text-gray-600">{t('registration.publishingTable.requiredOptional')}</th>
                          <th className="px-2 py-2 text-left font-medium text-gray-600">{t('registration.publishingTable.channel')}</th>
                          <th className="px-2 py-2 text-left font-medium text-gray-600">{t('registration.publishingTable.quantity')}</th>
                          <th className="px-2 py-2 text-left font-medium text-gray-600">{t('registration.publishingTable.reward')}</th>
                          <th className="px-2 py-2 text-left font-medium text-gray-600">{t('registration.publishingTable.startDate')}</th>
                          <th className="px-2 py-2 text-left font-medium text-gray-600">{t('registration.publishingTable.deadline')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campaign.publishingConditions.map((row: any, idx: number) => (
                          <tr key={idx} className="border-t border-gray-100">
                            <td className="px-2 py-2">{row.sequence}</td>
                            <td className="px-2 py-2">{row.isRequired ? t('registration.publishingTable.required') : t('registration.publishingTable.optional')}</td>
                            <td className="px-2 py-2">{String(t(`registration.contentTypes.${CONTENT_TYPE_KEY_MAP[row.publishingChannel] || row.publishingChannel}`, row.publishingChannel))}</td>
                            <td className="px-2 py-2">{row.contentQuantity}</td>
                            <td className="px-2 py-2">{row.rewardAmount ? `₩${formatNumber(row.rewardAmount)}` : '-'}</td>
                            <td className="px-2 py-2">{row.submissionStartDate ? formatDate(row.submissionStartDate) : '-'}</td>
                            <td className="px-2 py-2">{row.submissionDeadline ? formatDate(row.submissionDeadline) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Campaign Guide */}
              {(campaign.productIntroduction || campaign.contentWritingRequirements || campaign.additionalNotes) && (
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('registration.sections.campaignGuide')}</h3>
                  <div className="space-y-3 text-sm">
                    {campaign.productIntroduction && (
                      <div>
                        <span className="text-gray-600 block mb-1">{t('registration.fields.productIntroduction')}</span>
                        <p className="text-gray-800 whitespace-pre-wrap">{campaign.productIntroduction}</p>
                      </div>
                    )}
                    {campaign.contentWritingRequirements && (
                      <div>
                        <span className="text-gray-600 block mb-1">{t('registration.fields.contentWritingRequirements')}</span>
                        <p className="text-gray-800 whitespace-pre-wrap">{campaign.contentWritingRequirements}</p>
                      </div>
                    )}
                    {campaign.additionalNotes && (
                      <div>
                        <span className="text-gray-600 block mb-1">{t('registration.fields.additionalNotes')}</span>
                        <p className="text-gray-800 whitespace-pre-wrap">{campaign.additionalNotes}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Other Info */}
              {(campaign.recommendedKeywords || campaign.remarks) && (
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('registration.sections.otherInfo')}</h3>
                  <div className="space-y-3 text-sm">
                    {campaign.recommendedKeywords && (
                      <div>
                        <span className="text-gray-600 block mb-1">{t('registration.fields.recommendedKeywords')}</span>
                        <div className="flex flex-wrap gap-1">
                          {campaign.recommendedKeywords.split(',').map((kw: string, idx: number) => (
                            <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">{kw.trim()}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {campaign.remarks && (
                      <div>
                        <span className="text-gray-600 block mb-1">{t('registration.fields.remarks')}</span>
                        <p className="text-gray-800 whitespace-pre-wrap">{campaign.remarks}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Legacy: Description for old campaigns */}
              {!campaign.campaignName && campaign.description && (
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Description</h3>
                  <p className="text-gray-600 whitespace-pre-wrap">{campaign.description}</p>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Application Status */}
              {applicationSubmitted && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium text-green-800">Application Submitted!</span>
                  </div>
                </div>
              )}

              {/* Apply Button */}
              {canApply && !showApplicationForm && (
                <button
                  onClick={() => setShowApplicationForm(true)}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 font-medium transition-colors"
                >
                  Apply to Campaign
                </button>
              )}

              {/* Application Form */}
              {showApplicationForm && (
                <MatchApplicationForm
                  campaign={campaign}
                  onSubmit={handleApplicationSubmit}
                  onCancel={() => setShowApplicationForm(false)}
                />
              )}

              {/* Campaign Summary Sidebar */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Summary</h3>
                <div className="space-y-2 text-xs">
                  {campaign.brandName && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t('registration.fields.brandName')}</span>
                      <span className="font-medium">{campaign.brandName}</span>
                    </div>
                  )}
                  {campaign.numberOfRecruits != null && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t('registration.fields.numberOfRecruits')}</span>
                      <span className="font-medium">{formatNumber(campaign.numberOfRecruits)}명</span>
                    </div>
                  )}
                  {campaign.providedValueDescription && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t('registration.fields.providedValue')}</span>
                      <span className="font-medium">{campaign.providedValueDescription}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
